import { getEnumValue, TableColumnType } from '@/components';
import { PricingSettingsMatrixRowDto, SaleTaxApplyTo } from '@/core';
import { useFiltersFactory } from '@/utils';
import { isArray } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PricingSettingsApplyTo } from './PricingSettingsApplyTo';
import { PricingSettingsPercentageInput } from './PricingSettingsPercentageInput';
import { usePricingSettingsMatrixState } from './usePricingSettingsMatrixState';

export function usePricingSettingsColumns(
  matrixState: ReturnType<typeof usePricingSettingsMatrixState>,
) {
  const { t } = useTranslation();
  const { updateValue, defaults, value } = matrixState;

  const stringFilterOptions = useMemo(() => {
    const categoryValues = value.map((x) => x.categories[0].name);
    const subcategory1Values = value
      .map((x) => x.categories[1]?.name)
      .filter((x) => x && x.length > 0);
    const subcategory2Values = value
      .map((x) => x.categories[2]?.name)
      .filter((x) => x && x.length > 0);
    return {
      category: Array.from(new Set(categoryValues)),
      subcategory1: Array.from(new Set(subcategory1Values)),
      subcategory2: Array.from(new Set(subcategory2Values)),
    };
  }, [value]);

  const filters = useFiltersFactory(value, stringFilterOptions);

  const inheritedFromParentText = t('pricing.settings.inheritedFromParent');
  const usedDefaultValuesText = t('pricing.settings.usedDefault');

  const onHeaderCellStyle = () => ({
    style: {
      verticalAlign: 'top',
    },
  });

  return useMemo<TableColumnType<PricingSettingsMatrixRowDto>[]>(() => {
    const columns: Array<TableColumnType<PricingSettingsMatrixRowDto> | false> =
      [
        {
          title: t('pricing.settings.category'),
          key: 'category',
          ...filters.select((x) => x.categories[0].name, 'category'),
          exportValue: (record) => record.categories[0].name,
          render: (_, record) => {
            return record.categories[0].name;
          },
          onHeaderCell: onHeaderCellStyle,
        },
        {
          title: t('pricing.settings.subcategory1'),
          key: 'subcategory1',
          width: 160,
          ...filters.select((x) => x.categories[1]?.name, 'subcategory1'),
          exportValue: (record) => record.categories[1]?.name,
          render: (_, record) => {
            return record.categories[1]?.name || '-';
          },
          onHeaderCell: onHeaderCellStyle,
        },
        {
          title: t('pricing.settings.subcategory2'),
          key: 'subcategory2',
          width: 160,
          ...filters.select((x) => x.categories[2]?.name, 'subcategory2'),
          exportValue: (record) => record.categories[2]?.name,
          render: (_, record) => {
            return record.categories[2]?.name || '-';
          },
          onHeaderCell: onHeaderCellStyle,
        },
        {
          title: t('pricing.settings.defaults.offeredItem'),
          key: 'offeredItemConfiguration',

          children: [
            {
              title: t('pricing.settings.constructionMarkup'),
              key: 'constructionMarkup',
              exportValue: (record) =>
                record.value?.offeredItemConfiguration.construction?.value,
              width: 150,
              render: (_, item) => (
                <PricingSettingsPercentageInput
                  onSubmit={(value) =>
                    updateValue(
                      item,
                      'offeredItemConfiguration.construction',
                      value != null && value >= 0
                        ? {
                            value,
                            isOverridden: true,
                          }
                        : undefined,
                    )
                  }
                  initialValue={
                    item.value?.offeredItemConfiguration.construction
                      ?.isOverridden
                      ? item.value.offeredItemConfiguration.construction.value
                      : undefined
                  }
                  resetOnInitialValueChange
                  placeholder={
                    item.value?.offeredItemConfiguration.construction?.value?.toFixed(
                      6,
                    ) ?? defaults.offeredItemConstruction?.value.toFixed(6)
                  }
                  showTooltip={
                    item.value?.offeredItemConfiguration.construction
                      ?.isOverridden !== true
                  }
                  tooltipText={
                    <>
                      {!!item.value?.offeredItemConfiguration.construction
                        ? inheritedFromParentText
                        : usedDefaultValuesText}
                    </>
                  }
                />
              ),
            },
            {
              title: t('pricing.settings.residentMarkup'),
              key: 'residentMarkup',
              exportValue: (record) =>
                record.value?.offeredItemConfiguration.resident?.value,
              width: 150,
              render: (_, item) => (
                <PricingSettingsPercentageInput
                  onSubmit={(value) =>
                    updateValue(
                      item,
                      'offeredItemConfiguration.resident',
                      value != null && value >= 0
                        ? { value, isOverridden: true }
                        : undefined,
                    )
                  }
                  initialValue={
                    item.value?.offeredItemConfiguration.resident?.isOverridden
                      ? item.value?.offeredItemConfiguration.resident?.value
                      : undefined
                  }
                  resetOnInitialValueChange
                  placeholder={
                    item.value?.offeredItemConfiguration.resident?.value?.toFixed(
                      6,
                    ) ?? defaults.offeredItemResident?.value.toFixed(6)
                  }
                  showTooltip={
                    item.value?.offeredItemConfiguration.resident
                      ?.isOverridden !== true
                  }
                  tooltipText={
                    <>
                      {!!item.value?.offeredItemConfiguration.resident
                        ? inheritedFromParentText
                        : usedDefaultValuesText}
                    </>
                  }
                />
              ),
            },
          ] as TableColumnType<PricingSettingsMatrixRowDto>[],
        },
        {
          title: t('pricing.settings.defaults.specialRequest'),
          key: 'specialRequestConfiguration',
          children: [
            {
              title: t('pricing.settings.constructionMarkup'),
              key: 'specialRequestConstructionMarkup',
              exportValue: (record) =>
                record.value?.specialRequestConfiguration.construction?.value,
              width: 150,
              render: (_, item) => (
                <PricingSettingsPercentageInput
                  onSubmit={(value) =>
                    updateValue(
                      item,
                      'specialRequestConfiguration.construction',
                      value != null && value >= 0
                        ? {
                            value,
                            isOverridden: true,
                          }
                        : undefined,
                    )
                  }
                  initialValue={
                    item.value?.specialRequestConfiguration.construction
                      ?.isOverridden
                      ? item.value.specialRequestConfiguration.construction
                          .value
                      : undefined
                  }
                  resetOnInitialValueChange
                  placeholder={
                    item.value?.specialRequestConfiguration.construction?.value?.toFixed(
                      6,
                    ) ?? defaults.specialRequestConstruction?.value.toFixed(6)
                  }
                  showTooltip={
                    item.value?.specialRequestConfiguration.construction
                      ?.isOverridden !== true
                  }
                  tooltipText={
                    <>
                      {!!item.value?.specialRequestConfiguration.construction
                        ? inheritedFromParentText
                        : usedDefaultValuesText}
                    </>
                  }
                />
              ),
            },
            {
              title: t('pricing.settings.residentMarkup'),
              key: 'specialRequestResidentMarkup',
              exportValue: (record) =>
                record.value?.specialRequestConfiguration.resident?.value,
              width: 150,
              render: (_, item) => (
                <PricingSettingsPercentageInput
                  onSubmit={(value) =>
                    updateValue(
                      item,
                      'specialRequestConfiguration.resident',
                      value != null && value >= 0
                        ? { value, isOverridden: true }
                        : undefined,
                    )
                  }
                  initialValue={
                    item.value?.specialRequestConfiguration.resident
                      ?.isOverridden
                      ? item.value?.specialRequestConfiguration.resident?.value
                      : undefined
                  }
                  resetOnInitialValueChange
                  placeholder={
                    item.value?.specialRequestConfiguration.resident?.value?.toFixed(
                      6,
                    ) ?? defaults.specialRequestResident?.value.toFixed(6)
                  }
                  showTooltip={
                    item.value?.specialRequestConfiguration.resident
                      ?.isOverridden !== true
                  }
                  tooltipText={
                    <>
                      {!!item.value?.specialRequestConfiguration.resident
                        ? inheritedFromParentText
                        : usedDefaultValuesText}
                    </>
                  }
                />
              ),
            },
          ] as TableColumnType<PricingSettingsMatrixRowDto>[],
        },
        {
          title: t('pricing.settings.saleTax.value'),
          key: 'salesTaxValue',
          exportValue: (record) => record.value?.saleTax?.value,
          width: 150,
          onHeaderCell: onHeaderCellStyle,
          render: (_, item) => (
            <PricingSettingsPercentageInput
              onSubmit={(value) =>
                updateValue(item, 'saleTax', {
                  value,
                  isValueOverridden: value != null && value >= 0 ? true : false,
                  isApplyToOverridden: item.value?.saleTax?.isApplyToOverridden,
                  applyTo: item.value?.saleTax?.applyTo,
                })
              }
              initialValue={
                item.value?.saleTax?.isValueOverridden
                  ? item.value?.saleTax?.value
                  : undefined
              }
              resetOnInitialValueChange
              placeholder={
                item.value?.saleTax?.value?.toFixed(6) ??
                defaults.salesTax?.value.toFixed(6)
              }
              showTooltip={item.value?.saleTax?.isValueOverridden !== true}
              tooltipText={
                <>
                  {!!item.value?.saleTax
                    ? inheritedFromParentText
                    : usedDefaultValuesText}
                </>
              }
            />
          ),
        },
        {
          title: t('pricing.settings.saleTax.applyTo'),
          key: 'salesTaxApplyTo',
          onHeaderCell: onHeaderCellStyle,
          exportValue: (record) =>
            record.value?.saleTax?.applyTo
              ?.map((value) => getEnumValue(SaleTaxApplyTo, value, t))
              .join(' | '),
          width: 150,
          render: (_, item) => (
            <PricingSettingsApplyTo
              initialValue={
                item.value?.saleTax?.isApplyToOverridden
                  ? item.value?.saleTax?.applyTo
                  : undefined
              }
              placeholder={(item.value?.saleTax?.applyTo ?? defaults.applyTo)
                ?.map((value) => getEnumValue(SaleTaxApplyTo, value, t))
                .join(' | ')}
              onSubmit={(value) =>
                updateValue(item, 'saleTax', {
                  value: item.value?.saleTax?.value ?? null,
                  isValueOverridden: item.value?.saleTax?.isValueOverridden,
                  isApplyToOverridden: isArray(value) && value.length > 0,
                  applyTo: value,
                })
              }
              resetOnInitialValueChange
              showTooltip={item.value?.saleTax?.isApplyToOverridden !== true}
              tooltipText={
                <>
                  {!!item.value?.saleTax
                    ? inheritedFromParentText
                    : usedDefaultValuesText}
                </>
              }
            />
          ),
        },
      ];
    return columns.filter(
      (x) => !!x,
    ) as TableColumnType<PricingSettingsMatrixRowDto>[];
  }, [
    t,
    filters,
    defaults.offeredItemConstruction,
    defaults.offeredItemResident,
    defaults.specialRequestConstruction,
    defaults.specialRequestResident,
    defaults.salesTax,
    defaults.applyTo,
    inheritedFromParentText,
    usedDefaultValuesText,
    updateValue,
  ]);
}
